import React, { useContext, useEffect, useMemo, useState } from 'react';
import { RxFormContext } from 'src/core/forms';
import { map, tap, withLatestFrom } from 'rxjs';
import { expand } from 'src/core/forms/utils/expand';
import { Swatch } from 'src/components/ProductDetail/ProductSwatch';
import { Size } from 'src/components/ProductDetail/ProductSizes';
import { useConstant } from 'src/core/hooks/react-hooks';
import { BehaviorSubject, Subject } from 'rxjs';
import { selectVariantFromOptions } from 'src/components/ProductDetail/utils/selectVariantsFromOptions';
import { formToCustomisation } from 'src/components/BundleProduct/utils/formToCustomisation';
import { QuoteDataCartItemInterface } from 'swagger/ts/Definitions';
import { bundleCustomisationProps } from 'src/components/BundleProduct/utils/formToCustomisation';

import { BaseVariant } from '../ProductDetail/utils/product-detail.types';

import { FormData, FormItem, initialState, formToBundleOptions, createBundlePayload } from './utils/formDataToPayload';
import { BundleOptionsContext } from './BundleOptionsProvider';

export const BundleSelectionContext = React.createContext<{
    formData$: BehaviorSubject<FormData>;
    colorsMap: Record<string, Swatch>;
    sizesMap: Record<string, Size>;
    maxQty: number;
}>({
    formData$: new BehaviorSubject<FormData>(initialState),
    colorsMap: {},
    sizesMap: {},
    maxQty: 100,
});

export type SubmitFn = (payload: QuoteDataCartItemInterface, raw: Record<string, any>, formData: FormData) => void;

export type Props = {
    sku: string;
    id: number;
    submit$: Subject<any>;
    onSubmit: SubmitFn;
};

export const BundleSelectionProvider: React.FC<React.PropsWithChildren<Props>> = React.memo((props) => {
    const { sizes, swatches, maxQty, bundleOptions, variants } = useContext(BundleOptionsContext);

    /**
     * Create a mapping of colors by index
     */
    const colorsMap: Record<string, Swatch> = useMemo(() => {
        const colours = {};
        swatches.forEach((swatch) => {
            colours[swatch.value_index] = swatch;
        });
        return colours;
    }, [swatches]);

    /**
     * Create a mapping of colors by index
     */
    const sizesMap: Record<string, Size> = useMemo(() => {
        const outputSizes = {};
        sizes.forEach((size) => {
            outputSizes[size.id] = size;
        });
        return outputSizes;
    }, [sizes]);

    /**
     * The selected colour indexes
     */
    const formData$ = useFormItemsStream(sizesMap, colorsMap, props.sku, props.id);
    const { getValueStream } = useContext(RxFormContext);

    useEffect(() => {
        const sub = props.submit$
            .pipe(
                withLatestFrom(formData$, getValueStream()),
                tap(([, formData, rawValues]) => {
                    const options = formToBundleOptions(formData, bundleOptions, variants);
                    const values = expand(rawValues);
                    const customisation = formToCustomisation(values);
                    const payload = createBundlePayload(props.sku, customisation, options);
                    props.onSubmit(payload, values, formData);
                }),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [bundleOptions, formData$, getValueStream, props, props.sku, props.submit$, variants]);

    /**
     * The public API
     */
    const api = useMemo(() => {
        return { formData$, colorsMap, sizesMap, maxQty: maxQty };
    }, [formData$, colorsMap, sizesMap, maxQty]);

    return <BundleSelectionContext.Provider value={api}>{props.children}</BundleSelectionContext.Provider>;
});

export function useFormItemsStream(
    sizeMap: Record<string, Size>,
    colorMap: Record<string, Swatch>,
    parentSku: string,
    parentId: number,
): BehaviorSubject<FormData> {
    const { variants } = useContext(BundleOptionsContext);
    const { maxQty } = useContext(BundleSelectionContext);
    const { getValueStream } = useContext(RxFormContext);
    const state$ = useConstant(() => new BehaviorSubject<FormData>(initialState));
    useEffect(() => {
        const sub = getValueStream()
            .pipe(
                map(expand),
                map((values) => formValuesToFormData(values, maxQty, colorMap, sizeMap, variants, parentSku, parentId)),
                tap(state$),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [colorMap, getValueStream, maxQty, parentId, parentSku, sizeMap, state$, variants]);
    return state$;
}

/**
 * @param values
 * @param maxQty
 * @param colorMap
 * @param sizeMap
 * @param variants
 * @param parentSku
 * @param parentId
 */
export function formValuesToFormData(
    values: Record<string, any>,
    maxQty: number,
    colorMap: Record<string, Swatch>,
    sizeMap: Record<string, Size>,
    variants: BaseVariant[],
    parentSku: string,
    parentId: number,
): FormData {
    const artworksData: bundleCustomisationProps = {};
    for (const [key, value] of Object.entries(values)) {
        if (!isNaN(parseInt(key))) {
            artworksData[key] = value;
        }
    }

    const maybeColors = values.colours;
    if (!maybeColors) return initialState;
    const keys = Object.keys(maybeColors);
    const formItems = keys
        .filter((key) => maybeColors[key] === true)
        .map((colorFormKey) => {
            const [, colorIndex] = colorFormKey.split(':');
            const swatch = colorMap[colorIndex];
            const formItem: FormItem = {
                sizes: [],
                swatch: swatch,
            };
            const match = values[colorFormKey];
            if (match) {
                Object.keys(match).forEach((key) => {
                    const qty = parseInt(match[key]);
                    if (qty < 1) return;
                    const [, sizeIndex] = key.split(':');
                    const size = sizeMap[sizeIndex];
                    const fake_selection = {
                        color: swatch.value_index,
                        size: size.id,
                    };
                    const variant = selectVariantFromOptions(fake_selection as { [index: string]: number }, variants!);
                    if (variant) {
                        formItem.sizes.push({ qty, size: size });
                    }
                });
            }
            return formItem;
        });
    let sum = 0;
    formItems.forEach((item) => {
        item.sizes.forEach((size) => {
            sum += size.qty;
        });
    });
    return { items: formItems, totalQty: sum, disabled: sum > maxQty, parentSku, parentId, artworksData };
}

export function useIsSizesDisabled(defaultValue?): boolean {
    const { formData$ } = useContext(BundleSelectionContext);
    const [disabled, setIsDisabled] = useState(true);
    useEffect(() => {
        const sub = formData$
            .pipe(
                map((formData) => {
                    return formData.items.length === 0;
                }),
            )
            .subscribe(setIsDisabled);
        return () => sub.unsubscribe();
    }, [formData$]);
    return defaultValue ? false : disabled;
}

/**
 *
 */
export function useFormDataCausesRender(): FormData {
    const { formData$, maxQty } = useContext(BundleSelectionContext);
    const [state, setState] = useState(formData$.value);

    useEffect(() => {
        const sub = formData$.subscribe(setState);
        return () => sub.unsubscribe();
    }, [formData$, maxQty]);
    return state;
}
